import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { InsightsSection } from './components/InsightsSection';
import { TopicFilterChip } from './components/TopicFilterChip';
import { UnreadCountBadge } from './components/UnreadCountBadge';
import { InsightType, useUnifiedInsights } from './hooks/useUnifiedInsights.hook';
import { UNREAD_INSIGHTS_DISPLAY_CAP } from './utils/unreadCount';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Tabs, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const FILTER_PARAM = 'filter';
const TOPIC_PARAM = 'topic';
const DEFAULT_FILTER: InsightType = 'market_insights';

const isInsightType = (value: string | null): value is InsightType =>
  value === 'market_insights' || value === 'flagged' || value === 'alerts' || value === 'all';

type UnreadCount = {
  unread: number;
  capped: boolean;
  market_insights_unread: number;
  alerts_unread: number;
};

export const UnifiedAiInsightsPage = () => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'unified_ai_insights' });
  const [searchParams, setSearchParams] = useSearchParams();
  const { capture } = usePostHogAnalytics();

  const rawFilter = searchParams.get(FILTER_PARAM);
  const insightType: InsightType = isInsightType(rawFilter) ? rawFilter : DEFAULT_FILTER;
  const topic = searchParams.get(TOPIC_PARAM);

  const { data: unreadData } = useQuery<UnreadCount>({
    queryKey: [QueryKey.analytics.unreadInsightsCount],
  });
  const marketInsightsUnread = unreadData?.market_insights_unread ?? 0;
  const alertsUnread = unreadData?.alerts_unread ?? 0;

  const markAll = useMutation<
    { ok: boolean; count: number },
    Error,
    { scope: 'market_insights' | 'alerts' }
  >({
    mutationKey: [QueryKey.analytics.markAllInsightsReviewed],
  });

  const updateParams = useCallback(
    (next: { filter?: InsightType; topic?: string | null }) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.filter !== undefined) {
            if (next.filter === DEFAULT_FILTER) {
              params.delete(FILTER_PARAM);
            } else {
              params.set(FILTER_PARAM, next.filter);
            }
          }
          if (next.topic !== undefined) {
            if (next.topic) {
              params.set(TOPIC_PARAM, next.topic);
            } else {
              params.delete(TOPIC_PARAM);
            }
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const insights = useUnifiedInsights(insightType, topic);

  const showMarkAll =
    (insightType === 'market_insights' && marketInsightsUnread > 0) ||
    (insightType === 'alerts' && alertsUnread > 0);
  const markAllScope: 'market_insights' | 'alerts' =
    insightType === 'alerts' ? 'alerts' : 'market_insights';
  const markAllUnreadAtClick = markAllScope === 'alerts' ? alertsUnread : marketInsightsUnread;

  return (
    <div className="max-w-7xl mx-auto space-y-6 pt-4">
      <div className="flex items-center justify-between gap-4">
        <Tabs
          value={insightType}
          onValueChange={(value) => {
            if (!isInsightType(value)) return;
            capture(PostHogEventEnum.InsightsFilterTabClicked, { tab: value });
            updateParams({ filter: value });
          }}
        >
          <TabsList id="unified-insights-filter-tabs" className="gap-2">
            <TabsTrigger
              value="market_insights"
              data-testid="filter-tab-market-insights"
              className="gap-0"
            >
              {t('filter_tabs.market_insights')}
              <UnreadCountBadge
                unread={marketInsightsUnread}
                capped={marketInsightsUnread > UNREAD_INSIGHTS_DISPLAY_CAP}
                testId="filter-tab-market-insights-unread-badge"
              />
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="filter-tab-alerts" className="gap-0">
              {t('filter_tabs.alerts')}
              <UnreadCountBadge
                unread={alertsUnread}
                capped={alertsUnread > UNREAD_INSIGHTS_DISPLAY_CAP}
                testId="filter-tab-alerts-unread-badge"
              />
            </TabsTrigger>
            <TabsTrigger value="flagged" data-testid="filter-tab-flagged">
              {t('filter_tabs.flagged')}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="filter-tab-all">
              {t('filter_tabs.all')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {showMarkAll && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              capture(PostHogEventEnum.InsightsMarkAllReviewedClicked, {
                unread_before: markAllUnreadAtClick,
                scope: markAllScope,
              });
              markAll.mutate({ scope: markAllScope });
            }}
            disabled={markAll.isPending}
            data-testid="mark-all-reviewed"
          >
            {t('actions.mark_all_reviewed')}
          </Button>
        )}
      </div>

      {topic && (
        <TopicFilterChip
          topic={topic}
          onClear={() => {
            capture(PostHogEventEnum.InsightsTopicFilterCleared);
            updateParams({ topic: null });
          }}
        />
      )}

      <InsightsSection
        items={insights.items}
        insightType={insightType}
        isLoading={insights.isLoading}
        isFetchingNextPage={insights.isFetchingNextPage}
        hasNextPage={insights.hasNextPage}
        sentryRef={insights.sentryRef}
        onTopicClick={(value) => updateParams({ topic: value })}
      />
    </div>
  );
};
